import type { ReactNode } from 'react';
import type { DashboardSectionRegistration } from '../../../lib/flotiq-plugins/plugin-events/DashboardAddSectionEvent';
import type { dashboardSection } from './DefaultSections';

import { useMemo } from 'react';

import { DashboardAddSectionEvent } from '../../../lib/flotiq-plugins/plugin-events/DashboardAddSectionEvent';

import ElementFromPlugin from '../../../components/ElementFromPlugin/ElementFromPlugin';

import usePluginResults from '../../../hooks/usePluginResults';

import DashboardFirstSteps from './DashboardFirstSteps';
import DashboardLatestUpdates from './DashboardLatestUpdates';
import DashboardQuickLinks from './DashboardQuickLinks';
import DashboardStats from './DashboardStats';

type DashboardSectionRenderers = {
  renderers: Record<string, ReactNode>;
  sections: dashboardSection;
};

const useDashboardSectionRenderers = (
  testId: string,
): DashboardSectionRenderers => {
  /**
   * @emits FlotiqPlugins."flotiq.dashboard.section::add"
   */
  const pluginResults = usePluginResults(
    'flotiq.dashboard.section::add',
    DashboardAddSectionEvent,
  );

  return useMemo(() => {
    const sections: dashboardSection = {};
    const renderers: Record<string, ReactNode> = {
      stats: <DashboardStats testId={testId} />,
      'first-steps': <DashboardFirstSteps testId={testId} />,
      'latest-updates': <DashboardLatestUpdates testId={testId} />,
      'quick-links': <DashboardQuickLinks testId={testId} />,
    };

    pluginResults
      .flatMap((result) => (Array.isArray(result) ? result : [result]))
      .forEach((registration) => {
        const { id, label, content, visible } =
          registration as DashboardSectionRegistration;

        if (Object.hasOwn(renderers, id)) {
          throw new Error(`Dashboard section id "${id}" is already in use.`);
        }

        renderers[id] = (
          <section>
            <h2 className="text-xl text-slate-900 dark:text-slate-200">
              {label}
            </h2>
            <div className="mt-2 border rounded-lg border-slate-200 bg-white dark:bg-slate-950 dark:border-slate-800">
              <ElementFromPlugin results={[content]} />
            </div>
          </section>
        );
        sections[id] = { label, visible: visible ?? true };
      });

    return { renderers, sections };
  }, [pluginResults, testId]);
};

export default useDashboardSectionRenderers;
